import { Button, CheckBox, RadioButton, SelectDropDown } from '@wot/components';
import { ITutorExperience, ITutorExperienceDetails } from '@wot/typings';
import { isAfter } from 'date-fns';
import { parse } from 'date-fns/esm';
import { useEffect, useState } from 'react';
import { LOADING } from '../../../../assets/animated';
import { CLOSE_ICON } from '../../../../assets/icons';
import ProfileIssues from '../../ProfileIssues';
const year = new Date().getFullYear();

const years = Array.from(new Array(70), (_, index) => year - index);
const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

interface IWorkingSince {
  start: { month: string; year: number };
  end?: { month: string; year: number };
}

interface Props {
  experience?: ITutorExperienceDetails;
  onSave?: (data: ITutorExperienceDetails) => void;
  onSaveAndNext?: (data: ITutorExperienceDetails) => void;
  loading?: boolean;
}

export default function ExperienceDetails({ experience, onSave, onSaveAndNext, loading }: Props) {
  const [experienceList, setExperienceList] = useState<ITutorExperience[]>(experience?.data || []);

  const handleFormChange = (newValues: ITutorExperience, index?: number, remove?: number) => {
    let temp = [...experienceList];
    if (index) {
      if (remove) {
        temp = temp.filter((_, idx) => idx !== index - 1);
        setExperienceList(temp);
        return;
      }
      temp[index - 1] = newValues;
      setExperienceList(temp);
    } else {
      temp.push(newValues);
      setExperienceList(temp);
    }
  };

  const onSubmit = () => {
    onSave &&
      onSave({
        data: experienceList,
        status: experience?.status === 'verified' ? experience.status : 'pending',
        issues: []
      });
  };

  const onSubmitNext = () => {
    onSaveAndNext &&
      onSaveAndNext({
        data: experienceList,
        status: experience?.status === 'verified' ? experience.status : 'pending',
        issues: []
      });
  };

  return (
    <div className="h-full flex flex-col overflow-hidden pb-20">
      {experience?.status === 'issue' && experience.issues.length > 0 && <ProfileIssues issues={experience.issues} />}
      <div className="h-full w-full md:px-10 overflow-y-scroll custom-scrollbar">
        <CurrentForm handleFormChange={handleFormChange} />
        <div className="w-full flex flex-col gap-4 mb-4">
          {experienceList.map((exp, index) => (
            <ExperienceItem experience={exp} handleFormChange={handleFormChange} index={index + 1} key={index} />
          ))}
        </div>
      </div>
      {experienceList.length > 0 && (
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full bg-white py-5 border-t">
          <div className="max-w-sm w-full mx-auto flex gap-4 items-center justify-center">
            {loading ? (
              <div className="w-full flex items-center justify-center">
                <img src={LOADING} className="w-10 h-10" />
              </div>
            ) : (
              <>
                <div className="w-full block md:max-w-[164px]">
                  <Button block text={'Save'} onClick={() => onSubmit()} />
                </div>
                {onSaveAndNext && (
                  <div className="w-full block md:max-w-[164px]">
                    <Button block text={'Save & Next'} onClick={() => onSubmitNext()} />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CurrentForm({ handleFormChange }: { handleFormChange: (v: ITutorExperience, index?: number) => void }) {
  const [current, setCurrent] = useState<ITutorExperience>({
    type: 'online',
    organization: '',
    workingSince: {
      start: { month: '', year: 0 },
      end: { month: '', year: 0 },
    }
  });

  const [errors, setErrors] = useState<ExperienceFormErrors>();
  const [showNewForm, setShowNewForm] = useState<boolean>(false);

  return (
    <div className="w-full flex flex-col my-5 gap-7">
      <div className="w-full flex items-center justify-between">
        <h1 className="text-xl font-semibold">Total Teaching Experience</h1>
        <div className="max-w-xs">
          {!showNewForm && (
            <Button
              text="+ Add New"
              onClick={() => {
                setShowNewForm(!showNewForm);
              }}
            />
          )}
        </div>
      </div>

      {showNewForm && (
        <div className="border border-gray-300 rounded-md p-4">
          <div className="flex gap-4 items-start">
            <ExperienceForm
              type={current.type}
              setType={(value) => setCurrent({ ...current, type: value })}
              organisation={current.organization}
              setOrganisation={(value) => setCurrent({ ...current, organization: value })}
              workingSince={current.workingSince}
              setWorkingSince={(value) => setCurrent({ ...current, workingSince: value })}
              errors={errors}
            />
            <img
              src={CLOSE_ICON}
              className="w-5 cursor-pointer"
              onClick={() => {
                setShowNewForm(false);
                setCurrent({
                  type: 'online',
                  organization: '',
                  workingSince: {
                    start: { month: '', year: 0 },
                    end: { month: '', year: 0 },
                  }
                });
                setErrors({ organisation: false, workingSince: false });
              }}
            />
          </div>
          <div className="w-32 mt-5">
            <Button
              block
              text="Add"
              onClick={() => {
                const startDate = parse(`${current.workingSince.start.month} ${current.workingSince.start.year}`, 'MMMM yyyy', new Date());
                if (
                  !current.organization ||
                  !current.workingSince.start.month ||
                  (current.workingSince.end && isAfter(startDate, parse(`${current.workingSince.end.month} ${current.workingSince.end.year}`, 'MMMM yyyy', new Date())))
                ) {
                  setErrors({
                    ...errors,
                    organisation: !current.organization,
                    workingSince: true
                  });
                  return;
                }
                setErrors(undefined);
                handleFormChange(current);
                setCurrent({
                  type: 'online',
                  organization: '',
                  workingSince: {
                    start: { month: '', year: 0 },
                    end: { month: '', year: 0 },
                  }
                });

                setShowNewForm(false);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function ExperienceItem({
  experience,
  handleFormChange,
  index
}: {
  experience: ITutorExperience;
  index?: number;
  handleFormChange: (v: ITutorExperience, index?: number, remove?: number) => void;
}) {
  const [errors, setErrors] = useState<ExperienceFormErrors>();

  useEffect(() => {
    if (!experience.organization || !experience.workingSince) {
      setErrors({
        ...errors,
        organisation: !experience.organization,
        workingSince: !experience.workingSince
      });
      return;
    }
  }, []);

  return (
    <div className="w-full flex items-start gap-5 p-4 border border-gray-300 rounded-md">
      <ExperienceForm
        type={experience.type}
        setType={(value) => handleFormChange({ ...experience, type: value }, index)}
        organisation={experience.organization}
        setOrganisation={(value) => handleFormChange({ ...experience, organization: value }, index)}
        workingSince={experience.workingSince}
        setWorkingSince={(value) => handleFormChange({ ...experience, workingSince: value }, index)}
        errors={errors}
      />
      <img src={CLOSE_ICON} className="w-4 cursor-pointer" onClick={() => handleFormChange(experience, index, 1)} />
    </div>
  );
}

interface ExperienceFormErrors {
  organisation: boolean;
  workingSince: boolean;
}
interface ExperienceFormProps {
  type: 'online' | 'offline';
  setType: (value: 'online' | 'offline') => void;
  organisation: string;
  setOrganisation: (value: string) => void;
  workingSince: IWorkingSince;
  setWorkingSince: (value: IWorkingSince) => void;
  errors?: ExperienceFormErrors;
}

function ExperienceForm({
  type,
  setType,
  organisation,
  setOrganisation,
  workingSince,
  setWorkingSince,
  errors
}: ExperienceFormProps) {
  const [currentlyWorking, setCurrentlyWorking] = useState(!workingSince.end);
  return (
    <div className="w-full flex flex-col gap-7">
      <div className="flex flex-wrap gap-4 items-center">
        <h3 className="w-full md:w-2/5 text-sm shrink-0 font-semibold">Type of Work Experience</h3>
        <div className="h-full flex">
          <div className="flex items-center gap-2">
            <RadioButton
              checked={type === 'online'}
              onChange={(x) => x && setType('online')}
              name="radio-buttons"
              label="Online"
            />
            <RadioButton
              checked={type === 'offline'}
              onChange={(x) => {
                x && setType('offline');
              }}
              name="radio-buttons"
              label="Offline"
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center items-start lg:justify-between gap-2 lg:gap-0">
        <h3 className="w-full md:w-2/5 text-sm shrink-0">Organisation</h3>
        <div className="w-full flex flex-col">
          <div className="w-full max-w-md">
            <input
              type="text"
              value={organisation}
              onChange={(e) => setOrganisation(e.target.value)}
              placeholder="Organisation"
              className="w-full px-4 py-3 text-sm border border-neutralBlue1 placeholder:text-grey-400 rounded-md bg-white outline-none"
            />
          </div>
          {errors && errors?.organisation && (
            <p className="text-xs font-medium text-red-400">Please add organisation name</p>
          )}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-start items-start gap-2 lg:gap-0">
        <h3 className="w-full md:w-2/5 text-sm shrink-0">Working Since</h3>

        <div className="w-full flex flex-col gap-1">
          <div className="w-full flex flex-col gap-4 max-w-md">
            <div className="flex flex-col lg:flex-row lg:items-center items-start gap-4">
              <h3 className="text-sm">Start</h3>
              <SelectDropDown
                onChange={(value) =>
                  setWorkingSince({
                    ...workingSince,
                    start: { ...workingSince.start, month: value }
                  })
                }
                options={months}
                placeholder="Select Month"
                value={workingSince.start.month.toString()}
              />
              <SelectDropDown
                onChange={(value) =>
                  setWorkingSince({
                    ...workingSince,
                    start: { ...workingSince.start, year: Number(value) }
                  })
                }
                options={years.map((i) => i.toString())}
                placeholder="Select Year"
                value={workingSince.start.year > 0 ? workingSince.start.year.toString() : ''}
              />
            </div>
            {!currentlyWorking &&
              <div className="flex flex-col lg:flex-row lg:items-center items-start gap-4">
                <h3 className="text-sm">End</h3>
                <SelectDropDown
                  onChange={(value) =>
                    workingSince.end && setWorkingSince({
                      ...workingSince,
                      end: { ...workingSince.end, month: value }
                    })
                  }
                  options={months}
                  placeholder="Select Month"
                  value={workingSince?.end?.month.toString() || ''}
                />
                <SelectDropDown
                  onChange={(value) =>
                    workingSince.end &&
                    setWorkingSince({
                      ...workingSince,
                      end: { ...workingSince.end, year: Number(value) }
                    })
                  }
                  options={years.map((i) => i.toString())}
                  placeholder="Select Year"
                  value={workingSince.end && workingSince.end.year > 0 ? workingSince.end.year.toString() : ''}
                />
              </div>
            }

            <div className='flex items-center ml-auto gap-2'>
              <p className='text-sm'>Currently working here</p>
              <CheckBox
                defaultValue={currentlyWorking}
                onChange={(value) => {
                  if (!value) {
                    setWorkingSince({
                      ...workingSince,
                      end: { month: '', year: 0 },
                    })
                  } else {
                    setWorkingSince({
                      start: workingSince.start
                    })
                  }
                  setCurrentlyWorking(value)
                }}
              />
            </div>
          </div>
          {errors && errors?.workingSince && (
            <p className="text-xs font-medium text-red-400">Please Select a valid range</p>
          )}
        </div>
      </div>
    </div>
  );
}
